import { takeLatest, call, put, takeEvery } from 'redux-saga/effects';
import DOMPurify from 'dompurify';
import { 
  FETCH_DATA_TAG_REPORTS,
  FETCH_DATA_TAG_REPORT_CONTENT,
  SET_DATA_TAG_REPORTS_LOADER,
  SET_DATA_TAG_ALL_REPORTS,
  SET_DATA_TAG_SNACKBAR,
  SET_DATA_TAG_CONTENT_ACTION,
  DATA_TAG_REPORTS_API_URL,
  fetchDataTagReportContentAPI,
  fetchDataSourcesDataTag,
  SET_DATA_SOURCES_DATA_TAG,
  FETCH_DATA_SOURCES_DATA_TAG,
  SET_IS_DATABASE,
} from "../../features/Data Tag/DataTagConstants";
import { getAxiosHeaders, getService } from '../../library/RestAPI';


function* fetchDataTagReports(action) {
  yield put({ type: SET_DATA_TAG_REPORTS_LOADER, payload: true });

  const {
    reportType,
    periodType,
    page = 0,
    size = 20,
    search = "",
  } = action.payload || {};

  const queryParams = new URLSearchParams();

  if (reportType) {
    queryParams.append("reportType", reportType);
  }

  if (periodType) {
    queryParams.append("periodType", periodType);
  }

  queryParams.append("page", page);
  queryParams.append("size", size);

  if (search) {
    queryParams.append("search", search);
  }

  const options = {
    method: "GET",
    url: `${DATA_TAG_REPORTS_API_URL}?${queryParams.toString()}`,
    headers: getAxiosHeaders(true),
  };

  try {
    const res = yield getService(options);

    if (res && res.status === 200) {
      // API returns a paginated envelope:
      // {
      //   reports,
      //   page,
      //   size,
      //   totalElements,
      //   totalPages
      // }

      yield put({
        type: SET_DATA_TAG_ALL_REPORTS,
        payload: res?.data,
      });

      yield put({
        type: SET_DATA_TAG_REPORTS_LOADER,
        payload: false,
      });
    } else {
      yield put({
        type: SET_DATA_TAG_ALL_REPORTS,
        payload: {
          reports: [],
          totalElements: 0,
        },
      });

      yield put({
        type: SET_DATA_TAG_REPORTS_LOADER,
        payload: false,
      });

      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: {
          status: "error",
          message: "Failed to fetch reports",
        },
      });
    }
  } catch (error) {
    console.error("Error fetching Data Tag reports:", error);

    yield put({
      type: SET_DATA_TAG_ALL_REPORTS,
      payload: {
        reports: [],
        totalElements: 0,
      },
    });

    yield put({
      type: SET_DATA_TAG_REPORTS_LOADER,
      payload: false,
    });

    yield put({
      type: SET_DATA_TAG_SNACKBAR,
      payload: {
        status: "error",
        message: "Error fetching reports",
      },
    });
  }
}

const sanitizeReportHtml = (html) =>
  DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true });

function openReportPreviewWindow(html, previewTitle) {
  const previewWindow = window.open('', '_blank');

  if (!previewWindow) {
    return;
  }

  previewWindow.document.open();
  previewWindow.document.write(sanitizeReportHtml(html));
  previewWindow.document.close();

  if (previewTitle) {
    previewWindow.document.title = previewTitle;
  }
}

function printReportHtml(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      resolve();
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(cleanup, 1000);
      }
    };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(sanitizeReportHtml(html));
    doc.close();
  });
}

function downloadReportAsPdf(html, report) {
  const container = document.createElement('div');
  container.innerHTML = sanitizeReportHtml(html);
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  document.body.appendChild(container);

  const rangeSlug = report?.periodDisplayRange
    ? report.periodDisplayRange.replace(/\s+/g, '-')
    : report?.id || 'report';
  const fileName = `insights-report-${rangeSlug}.pdf`;

  // return html2pdf()
  //   .set({
  //     margin: 10,
  //     filename: fileName,
  //     html2canvas: { scale: 2, useCORS: true },
  //     jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  //   })
  //   .from(container)
  //   .save()
  //   .then(() => {
  //     document.body.removeChild(container);
  //   })
  //   .catch((err) => {
  //     document.body.removeChild(container);
  //     throw err;
  //   });
}

function* fetchDataTagReportContent(action) {
  const {
    reportId,
    mode,
    report,
    previewTitle,
  } = action.payload || {};

  yield put({
    type: SET_DATA_TAG_CONTENT_ACTION,
    payload: {
      reportId,
      mode,
    },
  });

  const options = {
    method: "GET",
    url: fetchDataTagReportContentAPI(reportId),
    headers: getAxiosHeaders(true),
    responseType: 'text',
  };

  try {
    const res = yield getService(options);

    if (res && res.status === 200 && typeof res.data === 'string') {
      const html = res.data;

      if (mode === 'preview') {
        openReportPreviewWindow(html, previewTitle);
      } else if (mode === 'print') {
        yield call(printReportHtml, html);
      } else if (mode === 'download') {
        yield call(downloadReportAsPdf, html, report);
      }
    } else {
      yield put({
        type: SET_DATA_TAG_SNACKBAR,
        payload: { status: "error", message: "Failed to load report content" },
      });
    }
  } catch (error) {
    console.error('Error fetching report content:', error);
    yield put({
      type: SET_DATA_TAG_SNACKBAR,
      payload: { status: "error", message: "Error loading report content" },
    });
  } finally {
    yield put({
      type: SET_DATA_TAG_CONTENT_ACTION,
      payload: {
        reportId: null,
        mode: null,
      },
    });
  }
}

function* fetchDataSourcesListDataTag (action) {
  try {
      const options = {
      method: "GET",
      url: fetchDataSourcesDataTag(),
      headers: getAxiosHeaders(true),
    };
    
    const response = yield getService(options);
    
    if (response && response.status === 200) {
      yield put({type: SET_DATA_SOURCES_DATA_TAG, payload: response?.data});
    } else {
      console.log("Something Went Wrong")
    }
  } catch (error) {
    console.error('Error viewing file:', error);
  }
}

export function* DataTagReportsSaga() {
  yield takeLatest(FETCH_DATA_TAG_REPORTS, fetchDataTagReports);
  yield takeLatest(FETCH_DATA_TAG_REPORT_CONTENT, fetchDataTagReportContent);
  yield takeEvery(FETCH_DATA_SOURCES_DATA_TAG, fetchDataSourcesListDataTag);
}